/**
 * Login Component
 * Handles user authentication with Cognito Hosted UI
 * 
 * Requirements: 9.1, 9.2
 */

import { useState } from 'react';
import { AuthService } from '../services/AuthService';
import { ErrorHandler } from '../utils/ErrorHandler';
import { GradientText } from './text/gradient-text';
import './Login.css';

function Login() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const authService = AuthService.getInstance();
  const errorHandler = ErrorHandler.getInstance();

  const handleLogin = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Redirect to Cognito Hosted UI
      // Requirements: 9.1, 9.2
      await authService.login();
    } catch (err) {
      // Handle login errors
      if (err instanceof Error) {
        setError(err.message);
        errorHandler.handleAuthError(err);
      } else {
        setError('Login failed. Please try again.');
        errorHandler.handleError(new Error('Login failed'), { component: 'auth' });
      }
      console.error('Login error:', err);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Massive SIFT Branding */}
        <div className="sift-hero">
          <div className="sift-title">
            <GradientText
              text="SIFT"
              gradient="linear-gradient(90deg, #3b82f6 0%, #a855f7 50%, #ec4899 100%)"
            />
          </div>
        </div>

        {/* Triality Labs Attribution */}
        <div className="triality-attribution">
          <span className="powered-by-text">powered by</span>
          <a 
            href="https://trialitylabs.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="triality-link"
          >
            <img 
              src="/assets/trialitylabs_logo.png" 
              alt="Triality Labs" 
              className="triality-logo-small"
            />
          </a>
        </div>

        <div className="login-form">
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <button
            type="button"
            className="login-button"
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="button-spinner"></span>
                Authenticating...
              </>
            ) : (
              'SIFT Login'
            )}
          </button>
        </div>

        <div className="login-footer">
          <p>Please log in to access the classification system</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
